import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { resetWhatsAppTemplate } from "@/components/WhatsAppButton";
import { Settings2, Loader2, Mail, Plus, Trash2, Globe } from "lucide-react";
import { resetSiteInfo } from "@/components/SiteFooter";
import { toast } from "sonner";

function QuickReplies({ list, onChange }) {
  const set = (i, k, v) => onChange(list.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <div data-testid="settings-quick-replies">
      <Label>WhatsApp quick replies</Label>
      <p className="text-xs text-muted-foreground mt-1 mb-2">Callers pick one of these in the WhatsApp box. The same {"{name}"}, {"{agent}"} and {"{product}"} words work here.</p>
      <div className="space-y-3">
        {list.map((t, i) => (
          <div key={i} className="border rounded-lg p-3 space-y-2" data-testid="quick-reply-row">
            <div className="flex gap-2"><Input value={t.title} placeholder="Title, e.g. Follow-up" onChange={(e) => set(i, "title", e.target.value)} data-testid={`quick-reply-title-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`quick-reply-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button></div>
            <Textarea rows={2} value={t.text} onChange={(e) => set(i, "text", e.target.value)} data-testid={`quick-reply-text-${i}`} />
          </div>
        ))}
      </div>
      {list.length < 20 && <Button variant="outline" size="sm" className="mt-2" onClick={() => onChange([...list, { title: "", text: "" }])} data-testid="quick-reply-add-btn"><Plus className="h-4 w-4 mr-1" />Add quick reply</Button>}
    </div>
  );
}

const SITE = [["phone", "Phone"], ["whatsapp", "WhatsApp number"], ["email", "Email"], ["address", "Office address"], ["hours", "Office hours"],
  ["facebook", "Facebook link"], ["instagram", "Instagram link"], ["youtube", "YouTube link"], ["linkedin", "LinkedIn link"], ["x", "X (Twitter) link"]];

function SiteInfoCard() {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/public/site-info").then(({ data }) => setV(data)).catch(() => setV({})); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/site-info", v); resetSiteInfo(); toast.success("Website details saved"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!v) return null;
  return (
    <Card className="p-6 max-w-xl mt-6" data-testid="site-info-card">
      <div className="flex items-center gap-2 mb-1 font-bold"><Globe className="h-4 w-4 text-primary" /> Website contact & social links</div>
      <p className="text-xs text-muted-foreground mb-4">Shown on the company website and the footer of the loan apply and status pages. Leave empty to hide. Social links must start with https://</p>
      <div className="grid sm:grid-cols-2 gap-3">{SITE.map(([k, l]) => (
        <div key={k} className={k === "address" ? "sm:col-span-2" : ""}><Label className="text-xs">{l}</Label>
          <Input className="mt-1" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`site-info-${k}`} /></div>))}</div>
      <Button className="mt-4" onClick={save} disabled={busy} data-testid="site-info-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save website details"}</Button>
    </Card>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)).catch(() => setS({})); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", s);
      setS((v) => ({ ...v, ...data }));
      resetWhatsAppTemplate();
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="flex justify-center h-96 items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure the sender used for all outgoing emails" icon={Settings2} />
      <Card className="p-6 max-w-xl">
        <div className="flex items-center gap-2 mb-4 font-bold"><Mail className="h-4 w-4 text-primary" /> Email Sender</div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-muted-foreground">Sender Name (shown to recipients)</Label>
            <Input className="mt-1.5" value={s.email_from_name || ""} onChange={(e) => setS({ ...s, email_from_name: e.target.value })} data-testid="settings-from-name" placeholder="OMI Fintech Insurance" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-muted-foreground">Reply-To Email (customer replies arrive here)</Label>
            <Input className="mt-1.5" value={s.email_reply_to || ""} onChange={(e) => setS({ ...s, email_reply_to: e.target.value })} data-testid="settings-reply-to" placeholder="support@omifintech.in" />
            <p className="text-xs text-muted-foreground mt-1.5">Emails are sent through OMI Fintech's verified mailing service for the best inbox delivery. The reply-to address controls where customer replies land and the name/address recipients see.</p>
          </div>
          <div>
            <Label>WhatsApp hello message</Label>
            <Textarea className="mt-1.5" rows={3} value={s.whatsapp_template || ""} onChange={(e) => setS({ ...s, whatsapp_template: e.target.value })} data-testid="settings-wa-template" />
            <p className="text-xs text-muted-foreground mt-1.5">Callers see this ready-made message on every lead card. Use {"{name}"}, {"{agent}"} and {"{product}"}; they get filled in for each lead. It opens in the caller's own WhatsApp.</p>
          </div>
          <QuickReplies list={s.whatsapp_templates || []} onChange={(l) => setS({ ...s, whatsapp_templates: l })} />
          <Button onClick={save} disabled={saving} data-testid="settings-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Settings"}</Button>
        </div>
      </Card>
      <SiteInfoCard />
    </div>
  );
}
